import { supabase } from './supabase';

interface LogDetails {
    [key: string]: any;
}

export const logAction = async (userId: string | undefined, action: string, details?: LogDetails | string) => {
    if (!userId) {
        console.warn('Logger: No user ID provided for action:', action);
        return;
    }

    try {
        const { error } = await supabase
            .from('system_logs')
            .insert({
                user_id: userId,
                action,
                details,
            });

        if (error) {
            console.error('Logger: Failed to save log', error);
        }
    } catch (err) {
        console.error('Logger: Unexpected error', err);
    }
};

interface StageEventParams {
    negociacaoId: number | string;
    userId?: string;
    fromStage?: string | null;
    toStage?: string | null;
    fromStatus?: string | null;
    toStatus?: string | null;
}

/**
 * Registra uma transição de etapa/status numa tabela estruturada
 * (`negociacao_stage_events`). Alimenta os modos Fluxo/aging/retorno do
 * Funil Dinâmico. Best-effort: nunca bloqueia a UI nem lança em caso de erro.
 */
export const logStageEvent = async ({ negociacaoId, userId, fromStage, toStage, fromStatus, toStatus }: StageEventParams) => {
    if (!userId) return; // RLS exige user_id = auth.uid()
    try {
        const { error } = await supabase
            .from('negociacao_stage_events')
            .insert({
                negociacao_id: Number(negociacaoId),
                user_id: userId,
                from_stage: fromStage ?? null,
                to_stage: toStage ?? null,
                from_status: fromStatus ?? null,
                to_status: toStatus ?? null,
            });
        if (error) console.warn('Logger: stage event não registrado', error.message);
    } catch (err) {
        console.warn('Logger: erro inesperado ao registrar stage event', err);
    }
};

interface TarefaStatusEventParams {
    tarefaId: number | string;
    userId?: string;
    fromStatus?: string | null;
    toStatus: string;
}

/**
 * Registra uma transição de solicitacao_status numa tabela estruturada
 * (`tarefa_status_events`), no mesmo padrão de `logStageEvent` pra negociações.
 * Alimenta o dashboard de Auditoria (tempo por etapa). Best-effort: nunca
 * bloqueia a UI nem lança em caso de erro.
 */
export const logTarefaStatusEvent = async ({ tarefaId, userId, fromStatus, toStatus }: TarefaStatusEventParams) => {
    if (!userId) return;
    try {
        const { error } = await supabase
            .from('tarefa_status_events')
            .insert({
                tarefa_id: Number(tarefaId),
                user_id: userId,
                from_status: fromStatus ?? null,
                to_status: toStatus,
            });
        if (error) console.warn('Logger: tarefa status event não registrado', error.message);
    } catch (err) {
        console.warn('Logger: erro inesperado ao registrar tarefa status event', err);
    }
};

export const logNegotiationAction = async (negotiationId: number, userId: string | undefined, action: string, details: string) => {
    if (!userId) {
        console.warn('Logger: No user ID provided for negotiation action:', action);
        return;
    }

    try {
        const { error } = await supabase
            .from('negociacao_logs')
            .insert({
                negociacao_id: negotiationId,
                user_id: userId,
                acao: action,
                detalhes: details
            });

        if (error) {
            console.error('Logger: Failed to save negotiation log', error);
        }
    } catch (err) {
        console.error('Logger: Unexpected error logging negotiation action', err);
    }
};

interface UsageEventParams {
  userId: string | undefined;
  modulo: string;
  funcionalidade?: string | null;
  acao?: string | null;
  origem: 'navegacao' | 'acao';
}

/**
 * Registra um evento de telemetria de uso (tabela `usage_events`), que
 * alimenta a aba Config > Uso. Best-effort: nunca bloqueia UI nem lança em
 * caso de erro — mesmo padrão de `logStageEvent`/`logTarefaStatusEvent`.
 */
export const logUsageEvent = async ({ userId, modulo, funcionalidade, acao, origem }: UsageEventParams) => {
  if (!userId) return; // RLS exige user_id = auth.uid()
  try {
    const { error } = await supabase
      .from('usage_events')
      .insert({
        user_id: userId,
        modulo,
        funcionalidade: funcionalidade ?? null,
        acao: acao ?? null,
        origem,
      });
    if (error) console.warn('Logger: usage event não registrado', error.message);
  } catch (err) {
    console.warn('Logger: erro inesperado ao registrar usage event', err);
  }
};
